import { LoadingOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Input,
  Row,
  Select,
  Upload,
  message,
  Typography,
} from "antd";
import Checkbox from "antd/lib/checkbox/Checkbox";
import { FC, useState, useEffect } from "react";

import { ICountry, IJobGrade, IModal } from "@types";
import CustomModal from "@/components/customModel";
import {
  ErrorServices,
  useCreateBenefitsPlanMutation,
  useFetchAllCompanyJobGradesQuery,
  useFetchBenefitPlanTypesQuery,
  useFetchCompanyCountriesQuery,
  useUpdateBenefitsPlanMutation,
} from "@services";
import { showSuccessPopup } from "@utils";
import { useTypedSelector } from "@hooks";
import {
  getSelectedCompanyFromStorage,
  getTokenFromSharedStorage,
} from "@/services/cookies";
import customErrorMessage from "@/components/CustomErrorMessage";
import { formatNumberWithCommas } from "../AddBenefitsPlan";

const { Option } = Select;
const { Text } = Typography;
interface IAddBenefitsPlan extends IModal {
  selectedBenefitPlan: any | null;
  setSelectedBenefitPlan: React.Dispatch<
    React.SetStateAction<any | null>
  >;
}

const INITIAL_CASH_ALLOWANCE: any = {
  name: "",
  grade: [],
  country_id: null,
  city: "",
  is_all_grade: false,
  is_global: false,
  value: null,
  description: "",
  category: null,
  // Non-Cash fields
  benefit_description: "",
  benefit_value: null,
  cost_to_employer: null,
  policy_manual: null,
};

const EditBenefitModal: FC<IAddBenefitsPlan> = ({
  isVisible,
  setIsVisible,
  selectedBenefitPlan
}) => {
  const { company } = useTypedSelector(state => state.auth.user!);
  const { id: company_id } = company!;
  const [loading, setLoading] = useState(false);

  const [benefitPlan, setBenefitPlan] = useState<any>(
    INITIAL_CASH_ALLOWANCE
  );

  const { data: planTypes, isLoading } = useFetchBenefitPlanTypesQuery();
  const [updatebenefitPlan, { isLoading: isUpdating }] =
    useUpdateBenefitsPlanMutation();

  const [gradeCountries, setGradeCountries] = useState<ICountry[]>([]);
  const { data: countriesData } = useFetchCompanyCountriesQuery({ company_id });
  const countries = countriesData?.data || [];

  const { data: jobGradesData } = useFetchAllCompanyJobGradesQuery({
    company_id,
  });
  const jobGrades = jobGradesData?.data || [];

  useEffect(() => {
    if (selectedBenefitPlan && jobGradesData?.data?.length > 0) {
      // 🔹 normalize country structure
      const normalizedCountries =
        selectedBenefitPlan?.countries?.map(c => ({
          id: c.id,
          name: c.country, // 🔥 KEY FIX
        })) || [];

      const formatted = {
        ...selectedBenefitPlan,
        value: selectedBenefitPlan?.detail?.value
          ? String(selectedBenefitPlan.detail.value).replace(/,/g, "")
          : "",
        description: selectedBenefitPlan?.detail?.description || "",
        benefit_type: selectedBenefitPlan?.detail?.benefit_type || "",
        employer_cost: selectedBenefitPlan?.detail?.employer_cost || "",
        calculation_basis:
          selectedBenefitPlan?.detail?.calculation_basis || null,
        // country_id: normalizedCountries.map(c => c.id), // IDs only
      };
      setBenefitPlan(formatted);
      handleGlobalChange(formatted?.is_global)
      handleGradeChangeForEdit(selectedBenefitPlan?.grades);
      if (normalizedCountries?.length > 0) {
        const countryIds = normalizedCountries.map(c => c.id);
        handleSelectChange("country_id", countryIds);
      }
    }
  }, [selectedBenefitPlan, jobGradesData?.data]);

  // Handle input & checkbox changes
  const handleChange = (e: any) => {
    const { name, value, checked } = e.target;
    setBenefitPlan(prev => ({
      ...prev,
      [name]: name?.includes("is_") ? checked : value,
    }));

    if (name === "is_all_grade" && checked) {
      setGradeCountries(countries);
    }
  };

  // Handle Select changes
  const handleSelectChange = (name: string, value: any) => {
    setBenefitPlan(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGradeChange = (selectedGrades: string[]) => {
    const selectedJobGrades = jobGrades.filter(jg =>
      selectedGrades?.includes(jg.grade)
    );
    const mergedCountries =
      selectedJobGrades?.flatMap(jg => jg.countries || []) || [];
    const uniqueCountries = mergedCountries.filter(
      (c, idx, self) => c && idx === self.findIndex(sc => sc.id === c.id)
    );
    setGradeCountries(uniqueCountries);
    setBenefitPlan(prev => ({
      ...prev,
      grade: selectedGrades,
      country_id: null, // reset selected country if grades change
      is_global: false,
    }));
  };
  const handleGlobalChange = (e: any) => {
    const isChecked = e;

    const allCountryIds = gradeCountries?.map(c => c.id) || [];

    setBenefitPlan((prev: any) => ({
      ...prev,
      is_global: isChecked,
      country_id: isChecked ? allCountryIds : [], // select all or clear
    }));
  };
  const handleGradeChangeForEdit = (selectedGrades: string[]) => {
    const selectedJobGrades = jobGrades.filter(jg =>
      selectedGrades?.includes(jg.grade)
    );
    const mergedCountries =
      selectedJobGrades?.flatMap(jg => jg.countries || []) || [];
    const uniqueCountries = mergedCountries.filter(
      (c, idx, self) => c && idx === self.findIndex(sc => sc.id === c.id)
    );
    setGradeCountries(uniqueCountries);

    setBenefitPlan(prev => ({
      ...prev,
      grade: selectedGrades,
      // country_id: null,
    }));
  };

  const uploadPolicy = async (): Promise<boolean> => {
    if (!benefitPlan?.policy_manual) return false; // no file = allow submit
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("policy_manual", benefitPlan.policy_manual);

      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/company/${company_id}/upload-policy/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getTokenFromSharedStorage()?.access}`,
            Accept: "application/json",
          },
          body: formData,
        }
      );
      setLoading(false);
      if (!res.ok) {
        throw new Error("Upload failed");
      }

      const data = await res.json();
      return data;
    } catch (error) {
      setLoading(false);
      return false;
    }
  };

  // Upload handler
  const handleUpload = (file: any) => {
    setBenefitPlan(prev => ({ ...prev, policy_manual: file }));
    return false; // prevent auto upload
  };


  // Validation
  const isValid = () => {
    const base =
      benefitPlan?.name &&
      benefitPlan?.category &&
      benefitPlan?.benefit_type &&
      gradeCountries.length > 0 &&
      (benefitPlan?.is_all_grade ||
        (benefitPlan?.grade && benefitPlan?.grade?.length > 0)) &&
      benefitPlan?.country_id &&
      benefitPlan?.description;
    if (!base) return false;

    if (benefitPlan?.benefit_type === "cash") {
      return benefitPlan?.calculation_basis && benefitPlan?.value;
    } else {
      return (
        benefitPlan?.description &&
        benefitPlan?.value &&
        benefitPlan?.employer_cost
      );
    }
  };

  const handleSubmitUpdate = async () => {
    try {
      // 1️⃣ Upload policy first
      const isUploaded: any = await uploadPolicy();
      if (
        !selectedBenefitPlan?.policy_manual_url &&
        !benefitPlan?.policy_manual
      ) {
        customErrorMessage("Policy is required. Benefit plan not updated.");
        return;
      }
      const payload: any = {
        name: benefitPlan?.name,
        benefit_type: benefitPlan?.benefit_type,
        category: benefitPlan?.category,
        grades: benefitPlan?.is_all_grade
          ? Array.from(new Set(jobGrades?.map((jg: any) => jg?.grade)))
          : Array.from(new Set(benefitPlan?.grade || [])),
        is_all_grade: benefitPlan?.is_all_grade,
        country_ids: benefitPlan?.country_id,
        is_global: benefitPlan?.is_global,
        description: benefitPlan?.description,
        city: benefitPlan?.city,
        plan_type_id: benefitPlan?.benefit_type === "cash" ? 1 : 2,
        value: benefitPlan?.value ? +benefitPlan?.value : null,
        employer_cost:
          benefitPlan?.benefit_type === "non_cash" &&
            benefitPlan?.employer_cost
            ? +benefitPlan.employer_cost
            : null,
        calculation_basis:
          benefitPlan?.benefit_type === "cash" &&
            benefitPlan?.calculation_basis
            ? benefitPlan?.calculation_basis
            : null,
        policy_manual_url: isUploaded?.data?.policy_manual_url
          ? isUploaded?.data?.policy_manual_url
          : selectedBenefitPlan?.policy_manual_url,
      };

      if (selectedBenefitPlan && benefitPlan?.name && benefitPlan?.city && benefitPlan?.description && benefitPlan?.category) {
        await updatebenefitPlan({
          company_id,
          id: selectedBenefitPlan?.id,
          body: payload,
        }).unwrap();
        showSuccessPopup({
          title: "Benefits Plan Updated",
          desc: "You have successfully updated the benefits plan.",
          role: "client",
        });
      }

      setIsVisible(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      ErrorServices(error);
    }
  };
  return (
    <CustomModal
      width="w-7/12"
      title={`Update Benefits Plan`}
      isVisible={isVisible}
      height="80vh"
      footer={
        <>
          <button
            className="bg-themeGrey text-themeBlack font-medium text-lg py-2 px-14 rounded-md"
            onClick={() => setIsVisible(false)}
          >
            {" "}
            <span> Cancel</span>
          </button>
          <button
            className={`bg-themeBlue text-white font-medium text-lg py-2 px-14 rounded-md `}
            type="primary"
            disabled={!isValid()}
            onClick={handleSubmitUpdate}
          >
            <span>
              {isUpdating || loading ? (
                <LoadingOutlined />
              ) : "Update"}
            </span>
          </button>
        </>
      }
      onClose={() => setIsVisible(false)}
    >
      <Row gutter={16}>
        <Col span={12}>
          <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
            Name
          </label>
          <Input
            className={`w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
            size="large"
            name="name"
            placeholder="Name of Benefit Plan"
            value={benefitPlan?.name}
            onChange={handleChange}
          />
        </Col>
        <Col span={12}>
          <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
            Category
          </label>
          <Select
            className="custom-select"
            size="large"
            placeholder="Select category"
            value={benefitPlan?.category}
            onChange={value => handleSelectChange("category", value)}
          >
            <Option value="statutory">Statutory</Option>
            <Option value="wellness">Wellness</Option>
            <Option value="health">Health</Option>
            <Option value="retirement">Retirement</Option>
          </Select>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
            Benefit Type
          </label>
          <Select
            className="custom-select"
            size="large"
            placeholder="Select benefit type"
            loading={isLoading}
            value={benefitPlan?.benefit_type}
            onChange={value => handleSelectChange("benefit_type", value)}
          >
            {planTypes?.data?.map((item: any) => (
              <Option
                key={item.id}
                value={item.name === "Cash" ? "cash" : "non_cash"}
              >
                {item.name}
              </Option>
            ))}
          </Select>
        </Col>
        {benefitPlan?.benefit_type === "cash" && (
          <Col span={12}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Calculation Basis
            </label>
            <Select
              className="custom-select"
              size="large"
              value={benefitPlan?.calculation_basis}
              onChange={value => handleSelectChange("calculation_basis", value)}
            >
              <Option value="percent_basic">% of Basic</Option>
              <Option value="percent_guaranteed">% of Guaranteed Cash</Option>
              <Option value="flat">Flat Amount</Option>
            </Select>
          </Col>
        )}
      </Row>

      {benefitPlan?.benefit_type === "cash" ? (
        <>
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
                Value
              </label>
              <Input
                className={`w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                size="large"
                name="value"
                placeholder="Value"
                value={formatNumberWithCommas(benefitPlan?.value)}
                onChange={handleChange}
              />
            </Col>
          </Row>
        </>
      ) : (
        <></>
      )}
      {benefitPlan?.benefit_type === "non_cash" && (
        <>
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
                Benefit Value
              </label>
              <Input
                className={`w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                size="large"
                name="value"
                placeholder="Benefit Value"
                value={formatNumberWithCommas(benefitPlan?.value)}
                onChange={handleChange}
              />
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
                Cost to Employer
              </label>
              <Input
                className={`w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                size="large"
                name="employer_cost"
                placeholder="Cost to Employer"
                value={formatNumberWithCommas(benefitPlan?.employer_cost)}
                onChange={handleChange}
              />
            </Col>
          </Row>
        </>
      )}
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
            Grades
          </label>
          <Select
            className="custom-select"
            size="large"
            mode="multiple"
            placeholder="Grades"
            disabled={benefitPlan?.is_all_grade}
            value={
              benefitPlan?.grade?.length ? benefitPlan?.grade : undefined
            }
            onChange={handleGradeChange}
          >
            {[
              ...new Set(
                jobGrades?.map(jgd => jgd?.grade)?.filter(Boolean) // remove null / undefined
              ),
            ].map(jg => (
              <Option key={jg} value={jg}>
                {jg}
              </Option>
            ))}
          </Select>
          <Row style={{ marginTop: 16 }}>
            <Checkbox
              checked={benefitPlan?.is_all_grade}
              name="is_all_grade"
              onChange={e => {
                handleChange(e);
                setBenefitPlan(prev => ({
                  ...prev,
                  country_id: null, // reset selected country if grades change
                  is_global: false,
                }));
                if (!e.target.checked) {
                  handleGradeChange(benefitPlan?.grade || []);
                } else {
                  handleGradeChange(
                    Array.from(
                      new Set(jobGrades?.map((jg: any) => jg?.grade))
                    ) || []
                  );
                }
              }}
            >
              All Grades
            </Checkbox>
          </Row>
        </Col>
        <Col span={12}>
          <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
            Country
          </label>
          <Select
            className="custom-select"
            size="large"
            mode="multiple"
            disabled={gradeCountries?.length === 0 || benefitPlan?.is_global}
            placeholder="Grades determine available countries"
            value={benefitPlan?.country_id || undefined}
            onChange={value => handleSelectChange("country_id", value)}
          >
            {gradeCountries?.map(c => (
              <Option key={c.id} value={c.id}>
                {c.name}
              </Option>
            ))}
          </Select>
          <Row style={{ marginTop: 16 }}>
            <Checkbox
              checked={benefitPlan?.is_global}
              name="is_global"
              disabled={gradeCountries?.length === 0}
              onChange={e => {
                handleGlobalChange(e?.target?.checked);
              }}
            >
              Global
            </Checkbox>
          </Row>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
            City
          </label>
          <Input
            className={`w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
            size="large"
            name="city"
            placeholder="City"
            value={benefitPlan?.city}
            onChange={handleChange}
          />
        </Col>
        <Col span={12}>
          <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
            Description
          </label>
          <Input.TextArea
            className={`w-full resize shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
            name="description"
            placeholder="Describe the benefit plan..."
            value={benefitPlan?.description}
            onChange={handleChange}
          />
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <>
            {selectedBenefitPlan?.policy_manual_url && (
              <Button className="mb-4" type="link">
                <a
                  href={selectedBenefitPlan?.policy_manual_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Existing Policy Manual
                </a>
              </Button>
            )}

            <Card bordered={false} style={{ background: "#fafafa" }}>
              <Text strong>Policy Manual</Text>
              <Text
                type="secondary"
                style={{ display: "block", marginBottom: 8 }}
              >
                Upload PDF or DOC/DOCX file
              </Text>

              <Upload
                beforeUpload={file => {
                  const fileExtension = file.name.split(".").pop()?.toLowerCase();
                  const allowedExtensions = ["pdf", "doc", "docx"];

                  if (!fileExtension || !allowedExtensions?.includes(fileExtension)) {
                    customErrorMessage("You can only upload PDF or DOC/DOCX files!");
                    return Upload.LIST_IGNORE;
                  }

                  handleUpload(file);
                  return false; // stop auto upload but keep valid file
                }}
                onRemove={() => {
                  setBenefitPlan(prev => ({ ...prev, policy_manual: null }));
                }}
                maxCount={1}
              >
                <button className="bg-themeBlue text-white font-medium text-lg py-1 px-6 rounded-md">
                  <UploadOutlined /> Upload File
                </button>
              </Upload>
            </Card>
          </>
        </Col>
      </Row>
    </CustomModal>
  );
};

export default EditBenefitModal;
